import React, { useMemo } from 'react';

import { useIntl, defineMessages } from 'react-intl';

import DepartmentIcon from '@atlaskit/icon/core/department';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Stack } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { TeamsLinkButton } from '@atlaskit/teams-app-internal-navigation/teams-link-button';

import { HierarchyReportsOverflowCard } from './HierarchyReportsOverflowCard';
import { HierarchyUserCard } from './HierarchyUserCard';
import { ChartActionsRow, ReportsList, VerticalChartLine } from './styles';
import { type HierarchyData, type HierarchyUser } from './types';

/** Max report cards before replacing the last slot with “See N more”. */
const MAX_REPORT_CARDS = 8;
/** When there are more than `MAX_REPORT_CARDS`, show this many user cards before the overflow tile. */
const REPORT_CARDS_BEFORE_OVERFLOW = 7;

interface HierarchyChartProps {
	data: HierarchyData;
	cloudId: string;
	accountId: string;
	testId?: string;
}

const messages = defineMessages({
	fullReportingLineLink: {
		id: 'reporting-lines.hierarchy-chart.full-reporting-line-link',
		defaultMessage: 'Full reporting line',
		description: 'Label on the full reporting line link',
	},
});

const NoopComponent = () => null;

function partitionReports(reports: HierarchyUser[]): {
	visibleReports: HierarchyUser[];
	overflow?: { hiddenPreview: HierarchyUser[]; hiddenCount: number };
} {
	if (reports.length <= MAX_REPORT_CARDS) {
		return { visibleReports: reports };
	}
	const hiddenReports = reports.slice(REPORT_CARDS_BEFORE_OVERFLOW);
	return {
		visibleReports: reports.slice(0, REPORT_CARDS_BEFORE_OVERFLOW),
		overflow: {
			hiddenPreview: hiddenReports,
			hiddenCount: hiddenReports.length,
		},
	};
}

/**
 * Displays a simple hierarchy chart (manager → user → reports) for a given user.
 *
 * @internal Not ready for consumption. Do not use in production.
 */
export const HierarchyChart: React.FC<HierarchyChartProps & React.RefAttributes<unknown>> =
	componentWithFG('pt_hierarchy_chart_enabled', NoopComponent, HierarchyChartComponent);

function HierarchyChartComponent({ data, cloudId, accountId, testId }: HierarchyChartProps) {
	const intl = useIntl();
	const { manager, user, reports = [] } = data;
	const hasReports = reports.length > 0;

	const { visibleReports, overflow } = useMemo(() => partitionReports(reports), [reports]);

	// NOTE: this is to change, its a temporary solution to navigate to the full reporting line
	// soon we will be opening the interactive and rich reporting lines modal (once created)
	const fullReportingLineNavigation = useMemo(() => {
		if (!cloudId || !accountId) {
			return undefined;
		}
		return navigateToTeamsApp({
			type: 'USER',
			payload: { userId: accountId },
			cloudId,
		});
	}, [cloudId, accountId]);

	const showFullReportingLineLink = !!(accountId && fullReportingLineNavigation?.href);

	return (
		<Stack alignInline="center" testId={testId}>
			{showFullReportingLineLink && fullReportingLineNavigation ? (
				<ChartActionsRow>
					<TeamsLinkButton
						href={fullReportingLineNavigation.href}
						intent="navigation"
						onClick={fullReportingLineNavigation.onNavigate}
						spacing="compact"
						iconBefore={DepartmentIcon}
					>
						{intl.formatMessage(messages.fullReportingLineLink)}
					</TeamsLinkButton>
				</ChartActionsRow>
			) : null}

			{manager && (
				<>
					<HierarchyUserCard user={manager} testId="hierarchy-manager-card" />
					<VerticalChartLine />
				</>
			)}

			<HierarchyUserCard user={user} selected testId="hierarchy-selected-card" />

			{hasReports && (
				<>
					<VerticalChartLine />
					<ReportsList testId="hierarchy-reports-list">
						{visibleReports.map((report) => (
							<HierarchyUserCard
								key={report.accountId}
								user={report}
								testId={`hierarchy-report-card-${report.accountId}`}
							/>
						))}
						{overflow ? (
							<HierarchyReportsOverflowCard
								hiddenReportsPreview={overflow.hiddenPreview}
								hiddenCount={overflow.hiddenCount}
								testId="hierarchy-reports-more-card"
							/>
						) : null}
					</ReportsList>
				</>
			)}
		</Stack>
	);
}
